import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ArrowRightLeft } from "lucide-react";

const RESOURCES = [
  { key: "leads", label: "Leads", nameKey: "business_name" },
  { key: "businesses", label: "Businesses", nameKey: "name" },
  { key: "opportunities", label: "Opportunities", nameKey: "name" },
  { key: "tasks", label: "Tasks", nameKey: "title" },
];

export default function AssignmentTool() {
  const [resource, setResource] = useState("leads");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [assignee, setAssignee] = useState("");

  const cfg = RESOURCES.find((r) => r.key === resource);
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);

  const load = useCallback(() => {
    api.get(`/${resource}`).then((r) => setRows(r.data.items)); setSelected([]);
  }, [resource]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.items.filter((u) => u.active !== false))); }, []);

  const filtered = rows.filter((r) => !q || (r[cfg.nameKey] || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.includes(r.id));

  const assign = async () => {
    if (!selected.length) { toast.error("Select records"); return; }
    if (!assignee) { toast.error("Select a salesperson"); return; }
    try {
      const { data } = await api.post("/records/assign", { resource, ids: selected, assigned_to: assignee === "__pool" ? null : assignee });
      toast.success(`Assigned ${data.count} ${resource}`); setSelected([]); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-assignments">
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <Select value={resource} onValueChange={setResource}>
          <SelectTrigger className="w-full lg:w-48" data-testid="assign-resource"><SelectValue /></SelectTrigger>
          <SelectContent>{RESOURCES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder={`Search ${cfg.label.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-search" />
        </div>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger className="w-full lg:w-52" data-testid="assign-assignee"><SelectValue placeholder="Assign to…" /></SelectTrigger>
          <SelectContent><SelectItem value="__pool">Unassigned pool</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={assign} disabled={!selected.length} data-testid="assign-confirm"><ArrowRightLeft className="h-4 w-4" />Assign {selected.length || ""}</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 border-b bg-slate-50/80 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Checkbox checked={allChecked} onCheckedChange={(v) => setSelected(v ? filtered.map((r) => r.id) : [])} data-testid="assign-select-all" />
          <span className="flex-1">Record</span><span className="w-40">Current Owner</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
          {filtered.length === 0 ? <p className="p-6 text-sm text-slate-400 text-center">No records.</p> : filtered.map((r) => (
            <label key={r.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`assign-row-${r.id}`}>
              <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
              <span className="flex-1 text-sm font-medium text-slate-800 truncate">{r[cfg.nameKey] || "—"}</span>
              <span className="w-40 text-xs text-slate-500 truncate">{umap[r.assigned_to] || "Unassigned"}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
